import { useState, useEffect, useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchDailyBaseline, computeTrend, makeLaneKey, type TrendDirection } from '@/lib/spotRateEngine';
import { fetchSpotRateCached } from '@/lib/spotRateTelemetry';
import { useRealtimeSelfHeal } from '@/hooks/useRealtimeSelfHeal';
import { parseYYYYMMDDToLocalDate } from '@/lib/dateOnly';

export type LoadStatus = "active" | "offered" | "expired" | "updates";

export interface MyLoadRow {
  id: string;
  ref: string;
  origin: string;
  originState: string;
  destination: string;
  destinationState: string;
  miles: number;
  equipment: string;
  equipmentRaw: string;
  rate: number;
  aiRate: number | null;
  /** Rate position vs current market: up = market higher than posted, down = market lower */
  aiDirection: "up" | "down" | "neutral" | null;
  /** 24h rolling market trend direction (null = insufficient prior data) */
  marketTrendDirection: TrendDirection | null;
  /** 24h rolling market trend change percent (null = insufficient prior data) */
  marketTrendPercent: number | null;
  marketRpm: number;
  ageHours: number;
  status: LoadStatus;
  pickupDate: string;
  deliveryDate: string;
  createdBy: string | null;
  weightLbs: number;
  lengthFt: number | null;
  pieces: number | null;
  commodity: string | null;
  specialInstructions: string | null;
  paymentTerms: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  referenceNumber: string | null;
  loadNumber: string;
  originLat: number | null;
  originLon: number | null;
  destLat: number | null;
  destLon: number | null;
  briefAudioPath: string | null;
  briefDurationSeconds: number | null;
}

const EQ_MAP: Record<string, string> = {
  van: 'Van', reefer: 'Reefer', flatbed: 'Flatbed',
  step_deck: 'Step Deck', power_only: 'Power Only',
  box_truck: 'Box Truck', hotshot: 'Hotshot',
  conestoga: 'Conestoga', rgn: 'RGN', tanker: 'Tanker',
  intermodal: 'Intermodal', car_hauler: 'Car Hauler',
  dump_trailer: 'Dump', lowboy: 'Lowboy', other: 'Other',
};

export function useMyLoads() {
  const { user } = useAuth();
  const [loads, setLoads] = useState<MyLoadRow[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.id;
  const qc = useQueryClient();

  const fetchLoads = useCallback(async () => {
    if (!userId) { setLoading(false); return; }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('loads')
        .select(`
          id, load_number, origin_city, origin_state,
          origin_latitude, origin_longitude,
          destination_city, destination_state,
          destination_latitude, destination_longitude,
          total_miles, equipment_type, rate_amount, status,
          created_at, pickup_date, delivery_date, created_by,
          weight_lbs, length_ft, pieces, commodity,
          special_instructions, payment_terms, contact_name,
          contact_email, contact_phone, reference_number,
          brief_audio_path, brief_duration_seconds,
          offers(id, status)
        `)
        .eq('created_by', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const now = Date.now();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Hide terminal-removed statuses from My Loads entirely.
      // Board-side is already gated to status === 'posted', so these never appear there either.
      const HIDDEN_STATUSES = new Set(['covered', 'unposted', 'deleted']);
      const visible = (data || []).filter(l => !HIDDEN_STATUSES.has(l.status as string));

      const rows: MyLoadRow[] = visible.map(load => {
        const ageMs = now - new Date(load.created_at || '').getTime();
        const ageHours = Math.floor(ageMs / (1000 * 60 * 60));

        const pickupLocal = parseYYYYMMDDToLocalDate(load.pickup_date);
        const isExpired = pickupLocal
          ? pickupLocal < today && load.status === 'posted'
          : false;

        const hasOffers = (load.offers || []).some(
          (o: any) => o.status === 'pending' || o.status === 'accepted_by_carrier' || o.status === 'countered'
        );
        const hasUpdates = (load.offers || []).some(
          (o: any) => o.status === 'countered'
        );

        let status: LoadStatus = 'active';
        if (load.status === 'expired' || load.status === 'cancelled' || isExpired) status = 'expired';
        else if (hasUpdates) status = 'updates';
        else if (hasOffers) status = 'offered';
        else if (load.status === 'posted') status = 'active';

        const miles = load.total_miles || 0;
        const rate = load.rate_amount || 0;
        // Initialize marketRpm as 0; will be populated by AI rate computation
        const marketRpm = 0;

        return {
          id: load.id,
          ref: load.load_number || '—',
          origin: `${load.origin_city || '?'}, ${load.origin_state || '?'}`,
          originState: load.origin_state || '',
          destination: `${load.destination_city || '?'}, ${load.destination_state || '?'}`,
          destinationState: load.destination_state || '',
          miles,
          equipment: EQ_MAP[load.equipment_type] || load.equipment_type || '—',
          equipmentRaw: load.equipment_type || '',
          rate,
          aiRate: null,
          aiDirection: null,
          marketTrendDirection: null,
          marketTrendPercent: null,
          marketRpm: Math.round(marketRpm * 100) / 100,
          ageHours,
          status,
          pickupDate: load.pickup_date || '',
          deliveryDate: load.delivery_date || '',
          createdBy: load.created_by || null,
          weightLbs: load.weight_lbs || 0,
          lengthFt: load.length_ft ?? null,
          pieces: load.pieces ?? null,
          commodity: load.commodity ?? null,
          specialInstructions: load.special_instructions ?? null,
          paymentTerms: load.payment_terms ?? null,
          contactName: load.contact_name ?? null,
          contactEmail: load.contact_email ?? null,
          contactPhone: load.contact_phone ?? null,
          referenceNumber: load.reference_number ?? null,
          loadNumber: load.load_number || '',
          originLat: load.origin_latitude ? Number(load.origin_latitude) : null,
          originLon: load.origin_longitude ? Number(load.origin_longitude) : null,
          destLat: load.destination_latitude ? Number(load.destination_latitude) : null,
          destLon: load.destination_longitude ? Number(load.destination_longitude) : null,
          briefAudioPath: (load as any).brief_audio_path ?? null,
          briefDurationSeconds: (load as any).brief_duration_seconds ?? null,
        };
      });

      setLoads(rows);

      // Fire-and-forget: compute AI suggested rates + market trends
      // Exclude ALL user loads from comp pool to prevent self-inclusion bias
      const userLoadIds = rows.map(r => r.id);
      computeAiRatesAndTrends(qc, rows, userLoadIds).then(updated => {
        if (updated) setLoads(updated);
      });
    } catch (err) {
      console.error('useMyLoads error:', err);
      setLoads([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLoads();

    if (!userId) return;

    const channel = supabase
      .channel('my-loads-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'loads', filter: `created_by=eq.${userId}` }, () => {
        setTimeout(fetchLoads, 500);
      })
      .subscribe((status) => { if (status === 'SUBSCRIBED') fetchLoads(); });

    return () => { supabase.removeChannel(channel); };
  }, [fetchLoads, userId]);

  useRealtimeSelfHeal(fetchLoads, !!userId);


  return { loads, loading, refetch: fetchLoads };
}

/* ─── Lane key for batching ─── */

/** Creates a deduplication key for lane + equipment combos using stable market identity */
function loadLaneKey(load: MyLoadRow): string | null {
  if (!load.originLat || !load.originLon || !load.destLat || !load.destLon || !load.equipmentRaw || load.miles <= 0) return null;
  const originCity = load.origin.split(', ')[0] || '';
  const destCity = load.destination.split(', ')[0] || '';
  if (!originCity || !load.originState || !destCity || !load.destinationState) return null;
  return makeLaneKey(originCity, load.originState, destCity, load.destinationState, load.equipmentRaw);
}

interface SpotResultWithTrend {
  aiRate: number;
  aiDirection: "up" | "down" | "neutral";
  marketRpm: number;
  marketTrendDirection: TrendDirection | null;
  marketTrendPercent: number | null;
}

/**
 * Compute spot rates + trends for loads, batched by lane/equipment key.
 * Same lane/equipment loads share one spot rate query and one baseline lookup.
 */
async function computeAiRatesAndTrends(qc: QueryClient, rows: MyLoadRow[], excludeLoadIds?: string[]): Promise<MyLoadRow[] | null> {
  const eligible = rows.filter(r => loadLaneKey(r) !== null);
  if (eligible.length === 0) return null;

  // Group by lane key to avoid duplicate queries
  const groups = new Map<string, MyLoadRow[]>();
  for (const load of eligible) {
    const key = loadLaneKey(load)!;
    const arr = groups.get(key) || [];
    arr.push(load);
    groups.set(key, arr);
  }

  const BATCH = 10;
  const results = new Map<string, SpotResultWithTrend>();
  const groupEntries = Array.from(groups.entries());

  for (let i = 0; i < groupEntries.length; i += BATCH) {
    const batch = groupEntries.slice(i, i + BATCH);
    const promises = batch.map(async ([key, groupLoads]) => {
      try {
        const representative = groupLoads[0];
        const [oCity] = representative.origin.split(', ');
        const [dCity] = representative.destination.split(', ');
        const input = {
          originLat: representative.originLat!,
          originLon: representative.originLon!,
          destLat: representative.destLat!,
          destLon: representative.destLon!,
          laneMiles: representative.miles,
          equipmentType: representative.equipmentRaw,
          originCity: oCity,
          originState: representative.originState,
          destCity: dCity,
          destState: representative.destinationState,
          excludeLoadIds,
        };

        // Compute spot rate via shared React Query cache (skip built-in trend,
        // we fetch daily baseline separately).
        const spotResult = await fetchSpotRateCached(qc, input, { skipTrend: true });
        if (!spotResult) return;

        // Fetch today's daily baseline for this lane key
        const baselineRpm = await fetchDailyBaseline(key);
        const trend = computeTrend(spotResult.marketRpm, baselineRpm);

        // Apply to all loads in this group
        for (const load of groupLoads) {
          // Rate position vs market (posted rate vs current market)
          const rateDirection: "up" | "down" | "neutral" =
            spotResult.marketRate > load.rate * 1.02 ? "up" :
            spotResult.marketRate < load.rate * 0.98 ? "down" : "neutral";

          results.set(load.id, {
            aiRate: spotResult.suggestedPostRate,
            aiDirection: rateDirection,
            marketRpm: spotResult.marketRpm,
            marketTrendDirection: trend?.direction ?? null,
            marketTrendPercent: trend?.changePercent ?? null,
          });
        }
      } catch {
        // Silently skip failures
      }
    });
    await Promise.all(promises);
  }

  if (results.size === 0) return null;

  return rows.map(r => {
    const ai = results.get(r.id);
    return ai ? {
      ...r,
      aiRate: ai.aiRate,
      aiDirection: ai.aiDirection,
      marketRpm: ai.marketRpm,
      marketTrendDirection: ai.marketTrendDirection,
      marketTrendPercent: ai.marketTrendPercent,
    } : r;
  });
}
